import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Activity,
    ShieldCheck,
    ShieldAlert,
    Loader2,
    RefreshCw,
    Search,
    Users,
    FileCheck2,
    FileClock,
    AlertTriangle,
    KeyRound,
    Download as DownloadIcon,
} from "lucide-react";
import { UserX, FileSignature } from "lucide-react";
import { PendingLettersPanel } from "../components/PendingLettersPanel";
import { toast } from "sonner";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortableHead } from "../components/SortableHead";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch { return iso; }
};

const downloadCsv = (rows) => {
    const header = "Cognome,Nome,Matricola,Consenso,Data consenso,Solleciti,Ultimo sollecito,Documenti,Scaricati,Pendenti,Presa visione richiesta,Confermati,Ack pendenti,Score\n";
    const body = rows.map((r) => [
        r.cognome, r.nome, r.matricola || "",
        r.consent_given ? "SI" : "NO",
        r.consent_at ? fmtDate(r.consent_at) : "",
        r.reminders_sent || 0,
        r.last_reminder_at ? fmtDate(r.last_reminder_at) : "",
        r.total_docs, r.downloaded, r.pending_download,
        r.requires_ack, r.acknowledged, r.pending_ack,
        r.score,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([header + body], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `conformita_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export default function ComplianceDashboardPage() {
    const [data, setData] = useState({ kpis: null, employees: [] });
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [includeCessati, setIncludeCessati] = useState(false);
    const [onlyNonCompliant, setOnlyNonCompliant] = useState(false);
    const [letters, setLetters] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/delivery/compliance/overview", {
                params: {
                    include_cessati: includeCessati,
                    only_non_compliant: onlyNonCompliant,
                    search: search.trim() || undefined,
                },
            });
            setData(data);
        } catch (e) {
            toast.error("Errore caricamento dashboard");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [includeCessati, onlyNonCompliant]);

    const handleSearch = (e) => {
        if (e.key === "Enter") fetchData();
    };

    const kpis = data.kpis;
    const rows = data.employees || [];

    const { sortedItems: rowsSorted, sortKey, sortDir, requestSort } = useSortableTable(rows, {
        customAccessors: {
            dipendente: (r) => `${r.cognome} ${r.nome}`.toLowerCase(),
            stato: (r) => r.is_non_compliant ? 0 : 1,  // non-conforme first when asc
        },
    });

    const kpiCards = useMemo(() => {
        if (!kpis) return [];
        return [
            {
                label: "Dipendenti totali",
                value: kpis.total_employees,
                sub: `${kpis.no_credentials} senza credenziali`,
                icon: Users,
                tone: "blue",
            },
            {
                label: "Informativa accettata",
                value: `${kpis.consent_given} / ${kpis.total_employees}`,
                sub: `${kpis.consent_rate}% completato`,
                icon: ShieldCheck,
                tone: kpis.consent_rate >= 80 ? "emerald" : kpis.consent_rate >= 40 ? "amber" : "red",
            },
            {
                label: "Mai effettuato accesso",
                value: kpis.consent_missing ?? (kpis.total_employees - kpis.consent_given),
                sub: (kpis.consent_missing ?? 0) === 0
                    ? "Tutti hanno fatto il primo accesso"
                    : `${kpis.consent_missing_notifiable ?? 0} sollecitati via email ogni 3 gg`,
                icon: UserX,
                tone: (kpis.consent_missing ?? 0) === 0 ? "emerald" : "red",
            },
            {
                label: "Documenti scaricati",
                value: `${kpis.docs_downloaded} / ${kpis.total_documents}`,
                sub: `${kpis.download_rate}% del totale consegnato`,
                icon: FileCheck2,
                tone: kpis.download_rate >= 60 ? "emerald" : kpis.download_rate >= 30 ? "amber" : "red",
            },
            {
                label: "Presa visione pendente",
                value: kpis.pending_acks,
                sub: kpis.pending_acks === 0 ? "Tutto confermato" : "Documenti critici",
                icon: FileClock,
                tone: kpis.pending_acks === 0 ? "emerald" : "amber",
            },
            {
                label: "Lettere da firmare",
                value: letters ? `${letters.pending} / ${letters.total}` : "…",
                sub: !letters ? "Proroghe e trasformazioni" : letters.pending === 0 ? "Tutte firmate" : "Sollecito automatico ogni 3 gg",
                icon: FileSignature,
                tone: !letters ? "blue" : letters.pending === 0 ? "emerald" : "amber",
            },
        ];
    }, [kpis, letters]);

    return (
        <div data-testid="compliance-dashboard-page">
            <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
                <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-12 w-12 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                            <Activity className="h-6 w-6" />
                        </div>
                        <div>
                            <h1 className="font-display text-2xl font-bold text-[#376DB3]">Dashboard Conformità HR</h1>
                            <p className="text-sm text-slate-600">
                                Stato consegna telematica e presa visione per tutta l'azienda.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadCsv(rows)}
                            disabled={!rows.length}
                            className="gap-2"
                            data-testid="compliance-export-csv"
                        >
                            <DownloadIcon className="h-4 w-4" />
                            Esporta CSV
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={fetchData}
                            disabled={loading}
                            className="gap-2"
                            data-testid="compliance-refresh"
                        >
                            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
                            Aggiorna
                        </Button>
                    </div>
                </div>

                {/* KPI cards */}
                <div className={`mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-6 ${kpis ? "" : "hidden"}`}>
                    {kpiCards.map((c) => {
                        const Icon = c.icon;
                        const toneClasses = {
                            blue: "border-blue-200 bg-blue-50 text-blue-900",
                            emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
                            amber: "border-amber-200 bg-amber-50 text-amber-900",
                            red: "border-red-200 bg-red-50 text-red-900",
                        }[c.tone] || "border-slate-200 bg-slate-50 text-slate-900";
                        return (
                            <div key={c.label} className={`rounded-lg border p-4 ${toneClasses}`} data-testid={`compliance-kpi-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="flex items-center justify-between">
                                    <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{c.label}</div>
                                    <Icon className="h-4 w-4 opacity-60" />
                                </div>
                                <div className="mt-2 font-mono-numbers text-2xl font-bold">{c.value}</div>
                                <div className="mt-1 text-xs opacity-75">{c.sub}</div>
                            </div>
                        );
                    })}
                </div>

                <PendingLettersPanel onLoaded={setLetters} />

                {/* Filters */}
                <div className="mb-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="relative flex-1 min-w-[260px]">
                            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <Input
                                placeholder="Cerca cognome, nome o matricola…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={handleSearch}
                                className="h-9 pl-8"
                                data-testid="compliance-search"
                            />
                        </div>
                        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm">
                            <input
                                type="checkbox"
                                checked={onlyNonCompliant}
                                onChange={(e) => setOnlyNonCompliant(e.target.checked)}
                                data-testid="compliance-only-non"
                            />
                            <span>Solo non conformi</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm">
                            <input
                                type="checkbox"
                                checked={includeCessati}
                                onChange={(e) => setIncludeCessati(e.target.checked)}
                                data-testid="compliance-include-cessati"
                            />
                            <span>Includi cessati</span>
                        </label>
                        <Button size="sm" onClick={fetchData} disabled={loading} className="h-9 bg-[#376DB3] text-white hover:bg-[#376DB3]/90" data-testid="compliance-apply">
                            Applica
                        </Button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-slate-50 hover:bg-slate-50">
                                <TableHead className="w-12">#</TableHead>
                                <SortableHead sortKey="dipendente" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort}>
                                    Dipendente
                                </SortableHead>
                                <SortableHead sortKey="consent_given" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    Consenso
                                </SortableHead>
                                <SortableHead sortKey="reminders_sent" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    Solleciti
                                </SortableHead>
                                <SortableHead sortKey="total_docs" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    Documenti
                                </SortableHead>
                                <SortableHead sortKey="downloaded" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    Scaricati
                                </SortableHead>
                                <SortableHead sortKey="pending_download" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    In attesa
                                </SortableHead>
                                <SortableHead sortKey="pending_ack" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-center">
                                    Ack pendenti
                                </SortableHead>
                                <SortableHead sortKey="stato" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="text-right">
                                    Stato
                                </SortableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                <TableRow><TableCell colSpan={9} className="py-12 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-slate-400" /></TableCell></TableRow>
                            ) : rowsSorted.length === 0 ? (
                                <TableRow><TableCell colSpan={9} className="py-12 text-center text-sm text-slate-500">
                                    Nessun dipendente corrisponde ai filtri.
                                </TableCell></TableRow>
                            ) : rowsSorted.map((r, idx) => (
                                <TableRow key={r.employee_id} data-testid={`compliance-row-${r.employee_id}`}>
                                    <TableCell className="font-mono-numbers text-xs text-slate-500">{idx + 1}</TableCell>
                                    <TableCell>
                                        <Link
                                            to={`/dipendenti/${r.employee_id}`}
                                            className="font-medium text-[#376DB3] hover:underline"
                                            data-testid={`compliance-link-${r.employee_id}`}
                                        >
                                            {r.cognome} {r.nome}
                                        </Link>
                                        <div className="text-xs text-slate-500">
                                            matr. {r.matricola || "—"}
                                            {r.cessato && <Badge variant="outline" className="ml-2 border-amber-300 bg-amber-50 text-[10px] text-amber-700">CESSATO</Badge>}
                                            {!r.has_credentials && <Badge variant="outline" className="ml-2 gap-1 border-slate-300 bg-slate-50 text-[10px] text-slate-600"><KeyRound className="h-2.5 w-2.5" />No login</Badge>}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        {r.consent_given ? (
                                            <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700" title={fmtDate(r.consent_at)}>
                                                <ShieldCheck className="h-3 w-3" />
                                                {fmtDate(r.consent_at)}
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700">
                                                <ShieldAlert className="h-3 w-3" />
                                                Mancante
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center" data-testid={`reminders-cell-${r.employee_id}`}>
                                        {r.reminders_sent > 0 ? (
                                            <div>
                                                <span className="font-mono-numbers font-medium text-slate-700">{r.reminders_sent}</span>
                                                <div className="text-[10px] text-slate-500">ultimo {fmtDate(r.last_reminder_at)}</div>
                                            </div>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-center font-mono-numbers font-medium">{r.total_docs}</TableCell>
                                    <TableCell className="text-center font-mono-numbers text-emerald-700">{r.downloaded}</TableCell>
                                    <TableCell className="text-center font-mono-numbers">
                                        <span className={r.pending_download > 0 ? "font-medium text-amber-700" : "text-slate-400"}>
                                            {r.pending_download}
                                        </span>
                                    </TableCell>
                                    <TableCell className="text-center font-mono-numbers">
                                        {r.pending_ack > 0 ? (
                                            <span className="font-medium text-red-700">{r.pending_ack} / {r.requires_ack}</span>
                                        ) : r.requires_ack > 0 ? (
                                            <span className="text-emerald-700">0 / {r.requires_ack}</span>
                                        ) : (
                                            <span className="text-slate-400">—</span>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-right">
                                        {r.is_non_compliant ? (
                                            <Badge variant="outline" className="gap-1 border-amber-300 bg-amber-50 text-amber-700">
                                                <AlertTriangle className="h-3 w-3" />
                                                Non conforme
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700">
                                                <ShieldCheck className="h-3 w-3" />
                                                Conforme
                                            </Badge>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
                <div className="mt-3 text-xs text-slate-500">
                    {rows.length} dipendenti mostrati · ordinati per non-conformità decrescente
                </div>
            </main>
        </div>
    );
}
